/**
 * useDepth — the page's single motion driver.
 *
 * The law: a layer's depth constant `k` drives four properties at once
 * (travel, scale, opacity, shadow), never one. A layer that lags must also
 * be larger, dimmer and softer — that is the difference between depth and
 * sliding wallpaper. Layers at k = 1.00 are never transformed, which is why
 * no type, hairline, row or control on this page ever moves.
 *
 * The architecture, and why each part exists:
 *   - ONE passive scroll listener. It flips a flag and nothing else.
 *   - ONE rAF loop. It reads scrollY once per frame and thereafter only
 *     writes. getBoundingClientRect() is never called inside it.
 *   - ONE IntersectionObserver gates which layers get written at all.
 *   - Per visible layer per frame: a single setProperty('--p', …). CSS does
 *     the arithmetic, so a stage with five planes costs one style write,
 *     not five.
 *   - The loop cancels itself on the absence of WORK — not the absence of
 *     visible layers. A parked page has layers on screen but nothing to
 *     write, and an earlier version kept looping there at 60fps on a
 *     motionless page. Scroll and pointer events set the dirty flag and
 *     restart it.
 *
 * Under prefers-reduced-motion nothing is bound at all: registerLayer
 * becomes a writer that stamps each layer's designed rest position, and the
 * loop, the scroll listener and the pointer listener are never created.
 */

const REDUCED =
  typeof matchMedia === "function" &&
  matchMedia("(prefers-reduced-motion: reduce)").matches;
const FINE =
  typeof matchMedia === "function" &&
  matchMedia("(hover: hover) and (pointer: fine)").matches;

/** Depth constants. Keep in step with the table in DESIGN.md §4.1. */
export const DEPTH = {
  LOCKED: 1.0,
  NEAR: 0.86,
  PLATE: 0.72,
  BACK: 0.58,
  GHOST: 0.4,
  WASH: 0.3,
};

const clamp01 = (n) => (n < 0 ? 0 : n > 1 ? 1 : n);

/* ── State ──────────────────────────────────────────────────── */

const layers = new Set(); // { el, k, axis, pointer, top, height, visible }
let raf = null;
let dirty = true;
let io = null;
let resizeTimer = null;
let bound = false;

// Pointer axis: a second, opt-in input, written only on the elements that
// use it — never on :root, which would invalidate style for the whole
// document on every frame.
let pointerLayers = [];
let ptx = 0;
let pty = 0;
let prx = 0;
let pry = 0;

let foldEl = null;

/* ── Measurement (never inside the loop) ────────────────────── */

function measure(layer) {
  const r = layer.el.getBoundingClientRect();
  const y = window.scrollY || window.pageYOffset || 0;
  layer.top = r.top + y;
  layer.height = r.height;
}

function measureAll() {
  readFold();
  layers.forEach(measure);
  dirty = true;
  start();
}

/* ── The loop ───────────────────────────────────────────────── */

// --fold is a token, so it is read once and on resize — never per frame.
// Reading computed style inside the loop is itself a style flush.
let foldPx = 620;
function readFold() {
  foldPx =
    parseFloat(
      getComputedStyle(document.documentElement).getPropertyValue("--fold")
    ) || 620;
}

function frame() {
  const vh = window.innerHeight;
  const y = window.scrollY || window.pageYOffset || 0;
  const didWork = dirty;

  if (dirty) {
    dirty = false;

    if (foldEl) {
      foldEl.style.setProperty("--p0", clamp01(y / foldPx).toFixed(4));
    }

    layers.forEach((l) => {
      if (!l.visible) return;
      const p = clamp01((y + vh - l.top) / (vh + l.height));
      l.el.style.setProperty("--p", p.toFixed(4));
    });
  }

  // Pointer lerp shares this loop rather than opening a second one.
  let settling = false;
  if (pointerLayers.length) {
    prx += (ptx - prx) * 0.08;
    pry += (pty - pry) * 0.08;
    settling = Math.abs(ptx - prx) > 0.05 || Math.abs(pty - pry) > 0.05;
    if (settling || pointerLayers.some((l) => !l.settled)) {
      for (const l of pointerLayers) {
        if (!l.visible) continue;
        l.el.style.setProperty("--mx", (prx * l.pointer).toFixed(2) + "px");
        l.el.style.setProperty("--my", (pry * l.pointer).toFixed(2) + "px");
        l.settled = !settling;
      }
    }
  }

  // Cancel on the absence of WORK, not the absence of visible layers. A
  // parked page has layers on screen but nothing to write — an earlier
  // version kept looping there and burned 60fps on a motionless page.
  // Any scroll or pointer move sets dirty and restarts us.
  if (!didWork && !settling) {
    raf = null;
    return;
  }
  raf = requestAnimationFrame(frame);
}

function start() {
  if (raf === null && !REDUCED) raf = requestAnimationFrame(frame);
}

function onScroll() {
  dirty = true;
  start();
}

function onPointer(e) {
  ptx = e.clientX / window.innerWidth - 0.5;
  pty = e.clientY / window.innerHeight - 0.5;
  start();
}

function onResize() {
  clearTimeout(resizeTimer);
  resizeTimer = setTimeout(measureAll, 150);
}

function onVisibility() {
  if (document.hidden) {
    if (raf !== null) cancelAnimationFrame(raf);
    raf = null;
  } else {
    dirty = true;
    start();
  }
}

function bind() {
  if (bound || REDUCED) return;
  bound = true;

  window.addEventListener("scroll", onScroll, { passive: true });
  window.addEventListener("resize", onResize);
  document.addEventListener("visibilitychange", onVisibility);
  if (FINE) window.addEventListener("pointermove", onPointer, { passive: true });

  io = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        for (const l of layers) {
          if (l.el !== entry.target) continue;
          l.visible = entry.isIntersecting;
          // will-change only while on screen, never globally, and only for
          // layers that asked for it: a cluster's back plate parallaxes
          // without being pinned to its own composited layer.
          if (l.promote) l.el.style.willChange = entry.isIntersecting ? "transform" : "";
          if (entry.isIntersecting) measure(l);
        }
      }
      dirty = true;
      start();
    },
    { rootMargin: "10% 0px" }   // pre-arm, but do not keep half a page promoted
  );

  if ("ResizeObserver" in window) {
    new ResizeObserver(onResize).observe(document.body);
  }
}

/* ── Public API ─────────────────────────────────────────────── */

/**
 * Register an element as a depth layer.
 * @param {HTMLElement} el
 * @param {object} opts
 * @param {number} opts.k        depth constant (see DEPTH)
 * @param {number} opts.pointer  pointer travel in px (0 = no pointer axis)
 * @param {string} opts.axis     'scroll' (default) or 'fold'
 * @param {boolean} opts.promote whether to set will-change while visible (default true)
 */
export function registerLayer(el, { k = DEPTH.PLATE, pointer = 0, axis = "scroll", promote = true } = {}) {
  if (!el) return () => {};

  if (REDUCED) {
    // Every layer renders at its exact designed rest position. No loop,
    // no listeners — they are never created rather than created and idle.
    el.style.setProperty("--p", "0.5");
    el.style.setProperty("--p0", "1");
    el.style.setProperty("--mx", "0px");
    el.style.setProperty("--my", "0px");
    el.style.setProperty("--k", String(k));
    return () => {};
  }

  bind();
  readFold();
  el.style.setProperty("--k", String(k));

  if (axis === "fold") {
    foldEl = el;
    el.style.setProperty("--p0", "0");
    dirty = true;
    start();
    return () => {
      if (foldEl === el) foldEl = null;
    };
  }

  const layer = { el, k, pointer, promote, visible: false, top: 0, height: 0, settled: false };
  measure(layer);
  layers.add(layer);
  if (pointer && FINE) pointerLayers = [...layers].filter((l) => l.pointer);
  io.observe(el);

  // Images change a layer's height after load; re-measure rather than
  // carrying a stale rect for the life of the page.
  el.querySelectorAll("img").forEach((img) => {
    if (!img.complete) img.addEventListener("load", () => measure(layer), { once: true });
  });

  dirty = true;
  start();

  return () => {
    io.unobserve(el);
    layers.delete(layer);
    el.style.willChange = "";
    pointerLayers = [...layers].filter((l) => l.pointer);
  };
}

/** Re-measure everything — call on locale change, since copy changes heights. */
export function remeasure() {
  if (!REDUCED) measureAll();
}

export const prefersReducedMotion = REDUCED;
